<template>
    <div>
        <div class="relative bg-gradient-to-b from-gray-800 to-black">
            <!-- Background -->
            <div aria-hidden="true" class="bg-shape-paths-black absolute h-full overflow-hidden w-full">
                <!-- Top-right: -->
                <div class="hidden at1024:absolute at1024:block at1024:-right-[100px] at1024:top-0 at1024:h-[777px] at1024:w-[480px] at1200:right-0">
                    <img
                        v-for="(image, index) in currentImages.tops"
                        :key="'top-' + index"
                        :src="`https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/${image.src}`"
                        :alt="'Top image ' + (index + 1)"
                        class="absolute duration-500 opacity-0 transition-opacity"
                        :class="{ 'opacity-100': visibleImg === image.visibleWhen }"
                        height="777"
                        width="480"
                        sizes="480px"
                    />
                </div>
                <!-- Bottom-left: -->
                <div class="hidden at1024:absolute at1024:block at1024:bottom-0 at1024:left-0 at1024:h-[663px] at1024:w-[305px]">
                    <img
                        v-for="(image, index) in currentImages.bottoms"
                        :key="'bottom-' + index"
                        :src="`https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/${image.src}`"
                        :alt="'Bottom image ' + (index + 1)"
                        class="absolute duration-500 opacity-0 transition-opacity"
                        :class="{ 'opacity-100': visibleImg === image.visibleWhen }"
                        height="663"
                        width="305"
                        sizes="305px"
                    />
                </div>
            </div>
            <!-- Content: -->
            <div class="wrap wrap-compact pb-12 pt-20 relative text-center text-white at1024:pb-11 at1024:pt-52">
                <!-- Search trigger: -->
                <button
                    @click="toggleProductSearchDialog($event,'product-search-drawer')"
                    class="bg-white h-14 mb-[60px] rounded-full w-72 opacity-90 type-primary at1024:text-xl at1024:w-104 hover:opacity-100 hover:text-gray-800"
                >
                    <BaseSvg name="icon-search" class="mr-1 w-6 -translate-y-0.5 at1024:w-8" role="presentation"/>
                    {{ $t('Search products') }}
                </button>
                <!-- Title: -->
                <h1 class="mb-14 text-[50px] leading-none at1024:mb-[150px] at1024:text-[80px]">
                    <i class="underline underline-offset-[10px] decoration-2 decoration-red">The</i> distributor of electrical and mechanical door hardware.
                </h1>
                <!-- Nav: -->
                <ul class="grid grid-cols-2 rounded-md text-sm at1024:grid-cols-4">
                    <li v-for="category in categories" class="pb-8 at1024:pt-4">
                        <a
                            :href="route(category.routeName)"
                            @mouseenter="setVisibleImg(category.name)"
                            class="relative flex flex-col group items-center text-gray-300 hover:text-white"
                        >
                            <BaseSvg :name="category.icon" class="mb-2 w-8" role="presentation"/>
                            {{ $t(category.display_name) }}
                            <span
                                class="absolute bg-red block -bottom-3 h-[6px] mt-2 opacity-0 rounded-full w-[6px] group-hover:opacity-100"
                                aria-hidden="true"
                            ></span>
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="bg-gray-100 bg-no-repeat bg-shape-s bg-[left_-200px] bg-[length:739px] at1024:bg-[11%_-700px] at1024:bg-[length:2244px]">
            <section class="bg-gray-100/80 pb-96 pt-[180px] relative at1200:overflow-hidden at1200:pt-[150px]">
                <img
                    src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_shot_1"
                    class="absolute right-7 top-[30px] w-36 at1200:-right-14 at1200:top-14 at1200:w-80"
                    height="315"
                    width="315"
                    sizes="315px"
                    loading="lazy"
                    alt="A smiling SECLOCK employee at their desk"
                    aria-hidden="true"
                />
                <h2 class="wrap wrap-compact bg-gray-100 leading-none mb-7 py-8 relative rounded-md text-3xl text-center text-gray at1024:leading-4.5 at1024:text-5xl">
                    The largest inventory, same-day shipping, the most expert
                    support. SECLOCK is here for you every step of the way.
                </h2>
                <p class="text-center">
                    <!-- inertia scroll handling messes with scrolling to in page elements, so scrolling manually -->
                    <a href="#why-seclock" @click.prevent="useHelpers().scrollToId('why-seclock')" class="btn-white-on-red">Learn why</a>
                </p>
                <img
                    src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_shot_3"
                    class="absolute bottom-32 -left-14 w-60 at1024:bottom-8 at1024:w-88 at1440:left-1/2 at1440:-translate-x-[776px]"
                    height="362"
                    width="362"
                    sizes="362px"
                    loading="lazy"
                    alt="A SECLOCK employee working on door hardware"
                    aria-hidden="true"
                />
                <img
                    src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_shot_2"
                    class="absolute bottom-64 right-5 w-24 at1024:bottom-44 at1024:right-0 at1024:w-48 at1440:right-1/2 at1440:translate-x-[720px]"
                    height="200"
                    width="200"
                    sizes="200px"
                    loading="lazy"
                    alt="A SECLOCK employee moving cardboard boxes with a forklift"
                    aria-hidden="true"
                />
            </section>
            <section class="bg-gray-100/80">
                <div class="wrap wrap-huge at768:pt-16 at1024:pt-28 at1024:relative at1024:z-10">
                    <h3 id="why-seclock" class="font-semibold leading-none mb-12 text-center text-3xl at768:text-4xl at1024:text-[80px]">
                        Why SECLOCK
                    </h3>
                    <div class="grid gap-4 max-w-[700px] mx-auto text-white at1024:gap-[30px] at1024:grid-cols-2 at1024:max-w-none">
                        <!-- Vast Inventory: -->
                        <div class="bg-gray-400 flex flex-col justify-between overflow-hidden p-6 relative rounded-sm text-center at768:p-8 at1024:col-span-2 at1024:h-[600px] at1024:items-start at1024:px-11 at1024:py-12 at1024:text-left">
                            <img
                                src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_card_1?v=1.0.0"
                                class="absolute h-full left-0 object-cover opacity-10 top-0 w-full at1024:opacity-100 at1440:left-16"
                                alt="A man pushing a cart with boxes through a warehouse"
                                height="429.5"
                                width="700"
                                sizes="700px"
                                loading="lazy"
                            />
                            <div class="mb-8 relative w-full at1024:max-w-[45%] at1024:pt-16">
                                <h3 class="font-semibold leading-none mb-4 text-2xl at768:text-3xl at1024:leading-[0.9] at1024:text-[70px]">
                                    Vast Inventory
                                </h3>
                                <p class="text-lg at768:text-xl at1024:text-2xl at1350:leading-[2.5rem] at1350:text-[2.5rem]">
                                    With the industry's most extensive in-stock
                                    inventory of products from the top
                                    manufacturers, you'll always get what you
                                    need.
                                </p>
                            </div>
                            <a
                                href="/catalog/manufacturers"
                                class="flex font-semibold gap-2 group items-center justify-center relative self-center text-lg at1024:justify-start at768:text-xl at1024:self-start hover:opacity-80"
                            >
                                View Our Catalog
                                <base-svg
                                    class="h-6 transition-transform group-hover:translate-x-1.5"
                                    name="icon-arrow-forward"
                                    aria-hidden="true"
                                />
                            </a>
                        </div>
                        <!-- Quick shipping: -->
                        <div
                            class="bg-brown flex flex-col justify-between overflow-hidden p-6 rounded-sm at1024:items-start at768:p-8 at1200:px-11 at1200:py-12 at1440:min-h-[650px]"
                        >
                            <div
                                class="relative text-center at1024:max-w-[85%] at1024:text-left at1350:max-w-[95%]"
                            >
                                <h3
                                    class="font-semibold leading-none mb-4 text-2xl at768:text-3xl at1024:leading-[72px] at1024:text-[70px]"
                                >
                                    Quick Shipping
                                </h3>
                                <p
                                    class="text-lg at768:text-xl at1350:text-2xl"
                                >
                                    With the most products on the shelf and
                                    just-in-time delivery, you can say goodbye
                                    to unnecessary delays and &#128075; hello to getting
                                    the job done sooner.
                                </p>
                            </div>
                            <img
                                src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/why-seclock-quick-shipping?v=1.0.0"
                                class="mt-8"
                                alt="A box with SECLOCK branding"
                                height="324"
                                width="660"
                                sizes="660px"
                                loading="lazy"
                            />
                        </div>
                        <!-- Technical expertise: -->
                        <div
                            class="bg-blue flex flex-col justify-between overflow-hidden p-6 relative rounded-sm at1024:items-start at768:p-8 at1200:px-11 at1200:py-12 at1440:min-h-[650px]"
                        >
                            <img
                                src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/why-seclock-technical-expertise?v=1.0.0"
                                class="absolute h-full right-0 object-cover opacity-10 top-0 at1024:top-16 at1024:opacity-100 at1024:-right-10 at1024:scale-75 at1350:right-0 at1350:scale-100 at1350:top-0"
                                alt="A collage of various ways we share our expertise"
                                height="639"
                                width="285"
                                sizes="285px"
                                loading="lazy"
                            />
                            <div
                                class="mb-8 relative text-center at1024:text-left at1440:max-w-[50%]"
                            >
                                <h3
                                    class="font-semibold leading-none mb-4 text-2xl at768:text-3xl at1024:leading-[72px] at1024:text-[70px]"
                                >
                                    Technical Expertise
                                </h3>
                                <p
                                    class="text-lg at768:text-xl at1024:max-w-[47.5%] at1350:max-w-[55%] at1350:text-2xl at1440:max-w-none"
                                >
                                    Field-experienced and factory-trained, our
                                    reps are here to provide solutions for any
                                    and all of your jobs.
                                </p>
                            </div>
                            <button class="flex font-semibold gap-2 group items-center justify-center relative text-lg at1024:justify-start at768:text-xl hover:opacity-80" @click.stop.prevent="showNewMessage">
                                Chat With An Expert
                                <base-svg
                                    class="h-6 transition-transform group-hover:translate-x-1.5"
                                    name="icon-arrow-forward"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                        <!-- Parts department: -->
                        <div
                            class="bg-gray-700 flex flex-col justify-between overflow-hidden p-6 relative rounded-sm text-center at768:p-8 at1024:col-span-2 at1024:h-[600px] at1024:items-start at1024:px-11 at1024:py-12 at1024:text-left"
                        >
                            <img
                                src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_card_parts?v=1.0.1"
                                class="absolute h-full right-0 object-cover opacity-10 top-0 at1024:object-contain at1024:object-right at1024:opacity-100"
                                alt="A keypad style door handle surrounded by manufacturer logos"
                                height="800"
                                width="800"
                                sizes="800px"
                                loading="lazy"
                            />
                            <div
                                class="mb-8 relative text-center at1024:max-w-[47.5%] at1024:text-left"
                            >
                                <h3
                                    class="font-semibold leading-none mb-4 text-2xl at768:text-3xl at1024:pt-16 at1024:text-4xl at1024:text-[70px]"
                                >
                                    Parts Department
                                </h3>
                                <p
                                    class="text-lg at768:text-xl at1350:text-2xl"
                                >
                                    No matter what part, component, or accessory
                                    you need, our dedicated parts department has
                                    it all. Source every finish, function,
                                    design, length, voltage, option, and
                                    more—with unrivaled technical expertise to
                                    back it up.
                                </p>
                            </div>
                            <a
                                href="/parts"
                                class="flex font-semibold gap-2 group items-center justify-center relative text-lg at1024:justify-start at768:text-xl hover:opacity-80"
                            >
                                See How
                                <base-svg
                                    class="h-6 transition-transform group-hover:translate-x-1.5"
                                    name="icon-arrow-forward"
                                    aria-hidden="true"
                                />
                            </a>
                        </div>
                        <!-- Source mode: -->
                        <div class="bg-gray-850 flex flex-col justify-between overflow-hidden p-6 relative rounded-sm text-center at768:p-8 at1024:col-span-2 at1024:h-[600px] at1024:items-start at1024:px-11 at1024:py-12 at1024:text-left">
                            <div class="mb-8 relative text-center at1024:max-w-[47.5%] at1024:text-left">
                                <h3 class="font-semibold leading-none mb-4 text-red text-2xl at768:text-3xl at1024:pt-16 at1024:text-4xl at1024:text-[70px] at1024:bg-top-16 at1024:bg-clip-text at1024:bg-cover at1024:bg-no-repeat at1024:bg-red-black-text-clip at1024:text-transparent at1024:bg-white">
                                    Source Mode
                                </h3>
                                <p class="text-lg at768:text-xl at1350:text-2xl">
                                    SECLOCK's revolutionary sourcing experience helps hardware professionals find, organize and source hardware for any project—from simple installations to complex openings. And as an extension of SECLOCK.com, it's intuitive and easy to use.
                                </p>
                            </div>
                            <a href="/source-mode" class="flex font-semibold gap-2 group items-center justify-center relative text-lg at1024:justify-start at768:text-xl hover:opacity-80">
                                Learn More
                                <base-svg class="h-6 transition-transform group-hover:translate-x-1.5" name="icon-arrow-forward" aria-hidden="true" />
                            </a>
                            <img src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/home_card_source_mode?v=1.0.0" class="absolute h-full -right-[150px] at1350:right-0 object-cover opacity-10 top-0 at1024:object-contain at1024:object-right at1024:opacity-100" alt="A collage of a laptop with various parts of the SECLOCK website floating around it" height="596" width="792" sizes="792px" loading="lazy" />
                        </div>
                    </div>
                </div>
            </section>
            <section
                class="pb-56 pt-32 relative at768:pb-[375px] at1024:pb-[610px] at1024:pt-44 at1200:pb-[615px] at1200:pt-[168px]"
            >
                <img
                    src="https://marketing-assets.seclock.com/image/upload/f_auto,q_auto,w_auto/partial-seclock-logo-rings?v=1.0.0"
                    class="absolute bottom-0 left-1/2 object-cover -translate-x-1/2"
                    alt=""
                    height="1329"
                    width="1792"
                    sizes="1792px"
                    loading="lazy"
                />
                <div class="wrap wrap-compact relative text-center">
                    <div class="absolute inset-0 bg-gray-100 blur-2xl"></div>
                    <h3
                        class="font-semibold leading-none mb-6 relative text-2xl at768:text-4xl at1024:text-[80px]"
                    >
                        The Leader for a Reason
                    </h3>
                    <p
                        class="mb-10 relative text-gray text-xl at768:text-2xl at1024:leading-[1.1] at1024:mb-12 at1024:text-[40px]"
                    >
                        For over 40 years, SECLOCK has delivered unmatched inventory depth and expert technical support. It's that combination that sets us apart, and sets your business on the path to success.
                    </p>
                    <a
                        href="/register"
                        class="btn-white-on-red relative"
                    >Register today</a
                    >
                </div>
            </section>
        </div>

        <section class="bg-black text-white">
            <div class="wrap wrap-huge py-24 at768:py-32 at1024:py-34">
                <h3 class="font-semibold leading-none mb-8 text-2xl at768:text-4xl at1024:text-[80px]">
                    Blog
                </h3>
                <ul class="gap-x-16 gap-y-8 grid relative at768:grid-cols-2">
                    <li v-if="blog?.featured" class="at768:row-start-1 at768:row-span-3">
                        <a :href="blog.featured.url" class="block group relative hover:text-red" :aria-label="blog.featured.title">
                            <img
                                :src="marketingAssetUrl(blog.featured?.image?.external_id)"
                                class="mb-4 rounded at768:mb-8 border border-gray-650 group-hover:border-red"
                                :alt="blog.featured?.image?.data?.alt_text ? blog.featured.image.data.alt_text : ''"
                                width="770"
                                sizes="770px"
                                loading="lazy"
                            />
                            <h3 class="font-semibold leading-none mr-12 text-xl at768:text-2xl at1024:text-3xl">
                                {{ blog.featured.title }}
                            </h3>
                            <p class="text-gray at1024:text-xl">
                                {{ DateTime.fromISO(blog.featured.published_at).toFormat('DDD') }} &bull; <span class="sr-only">In category: </span>{{ blog.featured.categories[0]?.name }}
                            </p>
                        </a>
                    </li>
                    <li v-for="post in blog.articles">
                        <a :href="post.url" class="block group relative hover:text-red" :aria-label="post.title">
                            <h3 class="font-semibold leading-none mr-12 text-xl at768:text-2xl at1024:text-3xl">
                                {{ post.title }}
                            </h3>
                            <p class="text-gray at1024:text-xl">
                                {{ DateTime.fromISO(post.published_at).toFormat('DDD') }} &bull; <span class="sr-only">In category: </span>{{ post.categories[0]?.name }}
                            </p>
                            <base-svg
                                class="absolute top-2 fill-red h-8 right-0 transition-transform group-hover:translate-x-1.5"
                                name="icon-arrow-forward"
                                aria-hidden="true"
                            />
                        </a>
                    </li>
                    <li class="absolute bottom-full right-0 -translate-y-8 at768:-translate-y-9 at1024:-translate-y-10">
                        <Link
                            :href="route('web.education.index')"
                            class="font-bold text-blue text-sm tracking-widest uppercase hover:underline flex items-center gap-2"
                            ><span>{{ $t('See all') }}</span> <BaseSvg name="ArrowForward" aria-hidden="true" class="h-5 w-5" />
                        </Link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
    <Meta
        title="Door Hardware | Door Hardware Supplier"
        description="Door Hardware Supply specialist SECLOCK is the leading B2B wholesale supplier of all things door hardware. Contact via 800.847.5625 for inquiries."
        :jsonLdBreadcrumbs="[
            { title: 'Home', url: 'https://www.seclock.com' }
        ]"
    />
</template>

<script setup>
import {Link} from "@inertiajs/vue3";
import {computed, onMounted, ref} from "vue";
import {BaseSvg, Meta} from "@/Components";
import {useAssetTransforms, useChat, useHelpers, useProductSearchDialog, useUser} from "@/Composables";
import {DateTime} from "luxon";
import route from "ziggy-js";

const props = defineProps(['blog']);

const { settings, user } = useUser();
const { toggleProductSearchDialog } = useProductSearchDialog();
const { showNewMessage } = useChat();
const { marketingAssetUrl } = useAssetTransforms();

const categories = [
    {
        name: 'products',
        display_name: 'Products',
        routeName: 'web.catalog.products.index',
        icon: 'icon-products-filled',
    },
    {
        name: 'manufacturers',
        display_name: 'Manufacturers',
        routeName: 'web.catalog.manufacturers.index',
        icon: 'icon-manufacturers-filled',
    },
    {
        name: 'catalogs',
        display_name: 'Price Books & Catalogs',
        routeName: 'web.catalog.price-books.index',
        icon: 'icon-catalogs-book',
    },
    {
        name: 'resources',
        display_name: 'Resources',
        routeName: 'web.catalog.resources.index',
        icon: 'icon-collection-bookmark-filled',
    },
];

const imageTypes = {
    default: {
        tops: [
            {
                src: 'home-hero-default-products-top',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-default-manufacturers-top',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-top',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-top',
                visibleWhen: 'resources',
            },
        ],
        bottoms: [
            {
                src: 'home-hero-default-products-bottom',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-default-manufacturers-bottom',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-bottom',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-bottom',
                visibleWhen: 'resources',
            },
        ],
    },
    locksmithsContractHardware: {
        tops: [
            {
                src: 'home-hero-l-ch-products-top',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-l-ch-manufacturers-top',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-top',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-top',
                visibleWhen: 'resources',
            },
        ],
        bottoms: [
            {
                src: 'home-hero-l-ch-products-bottom',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-l-ch-manufacturers-bottom',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-bottom',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-bottom',
                visibleWhen: 'resources',
            },
        ]
    },
    glass: {
        tops: [
            {
                src: 'home-hero-g-products-top',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-g-manufacturers-top',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-top',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-top',
                visibleWhen: 'resources',
            },
        ],
        bottoms: [
            {
                src: 'home-hero-g-products-bottom',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-g-manufacturers-bottom',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-bottom',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-bottom',
                visibleWhen: 'resources',
            },
        ],
    },
    systemIntegrators: {
        tops: [
            {
                src: 'home-hero-si-products-top',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-si-manufacturers-top',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-top',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-top',
                visibleWhen: 'resources',
            },
        ],
        bottoms: [
            {
                src: 'home-hero-si-products-bottom',
                visibleWhen: 'products',
            },
            {
                src: 'home-hero-si-manufacturers-bottom',
                visibleWhen: 'manufacturers',
            },
            {
                src: 'home-hero-default-books-catalogs-bottom',
                visibleWhen: 'catalogs',
            },
            {
                src: 'home-hero-default-resources-bottom',
                visibleWhen: 'resources',
            },
        ],
    },
};

const customerType = ref(null);

onMounted(() => {
  if(user.value) {
    customerType.value = user.value?.team?.customer?.cu_group;
  }
});

function getImageTypeForGroup(group) {
  const groupMap = {
    C: 'locksmithsContractHardware',
    G: 'glass',
    S: 'systemIntegrators',
  };
  return groupMap[group] || 'default';
}

const visibleImg = ref("products");

const currentImages = computed(() => imageTypes[getImageTypeForGroup(customerType.value)]);

const setVisibleImg = (value) => visibleImg.value = value;
</script>
